import styles from '@components/components/EnergieSparCheck/WarmWasserCheck/WarmWasserCheck.module.scss';
import { Container, InputFieldNumeric } from '@components/components';
import {
  getResultsWasser,
  questions,
} from '@components/components/EnergieSparCheck/WarmWasserCheck/WarmWasserCheck.config.js';
import FinalStep from '@components/components/EnergieSparCheck/WarmWasserCheck/FinalStep';
import { useSessionStorage, useTracking } from '@components/hooks';
import React, { useEffect, useState } from 'react';
import {
  useFormStore,
  Button as BaseButton,
  Form,
  FormSubmit,
} from '@ariakit/react';
import {
  Button,
  Card,
  CardBody,
  ChoiceCard,
  Content,
  Heading,
  ProgressBar,
} from '@wienenergiegithub/ui-next';
import classNames from 'classnames';

const OptionDecider = ({ options, type, name, onClick }) => {
  const [sliderVal, setSliderVal] = useState(5);
  const sliderProps = {
    min: 5,
    max: 60,
    step: 1,
    defaultValue: 5,
    onChange: e => setSliderVal(e.target.value),
  };

  const val = sliderVal;
  const min = 5;
  const max = 60;
  const newValue = Number(((val - min) * 100) / (max - min)); // magic numbers
  const left =
    sliderVal < 40
      ? `calc(${newValue}% + (${10 - newValue * 0.15}px))`
      : `calc(${newValue}% + (${1 - newValue * 0.2}px))`;
  const bubbleStyle = {
    left,
  };

  const [values, setValues] = useState({ shower: '' });
  const form = useFormStore({ values, setValues });
  const errors = form.useState('errors');

  form.useValidate(() => {
    if (!values.shower || values.shower < 1 || values.shower > 300) {
      form.setError(
        'shower',
        'Bitte geben Sie uns die Anzahl aller Duschvorgänge in Ihrem Haushalt ein. Beispiel: Sie sind ein 2 Personen Haushalt und beide Personen duschen 7 Mal pro Woche, dann geben Sie die Zahl „14“ ein. (300 ist Maximumwert)',
      );
    }
  });

  const handleShowerSubmit = () => {
    onClick('shower', values.shower, 'shower');
  };

  return (
    <>
      {type === 'input' && (
        <Form className={styles.inputWrapper} store={form}>
          <Card>
            <CardBody>
              <InputFieldNumeric
                label=" Bitte geben Sie eine Zahl ein:"
                name="shower"
                required
                store={form}
                variant="closed"
              />
            </CardBody>
          </Card>

          <FormSubmit
            disabled={!values.shower || errors.shower}
            render={
              <Button className={styles.button} onClick={handleShowerSubmit}>
                Weiter
              </Button>
            }
            store={form}
          />
        </Form>
      )}
      {type === 'slider' && (
        <div className={styles.inputWrapper}>
          <Card className={styles.rangeCard}>
            <div className={styles.rangeWrapper}>
              <input type="range" {...sliderProps} className={styles.slide} />
              <output
                className={styles.bubble}
                style={{
                  ...bubbleStyle,
                }}
              >
                <p>{sliderVal}</p>
              </output>
            </div>
            <div className={styles.sliderValues}>
              <Heading as="h5" noSpacing size="h7">
                5
              </Heading>

              <Heading as="h5" noSpacing size="h7">
                60
              </Heading>
            </div>
          </Card>

          <Button
            className={styles.button}
            disabled={sliderVal === 0}
            onClick={() => onClick('showerTime', sliderVal, 'showerTime')}
          >
            Weiter
          </Button>
        </div>
      )}
      {type === 'choiceCard' &&
        options?.map(({ text, param }) => (
          <ChoiceCard
            as={BaseButton}
            key={text}
            onClick={() => onClick(text, param, name)}
            title={text}
          />
        ))}
    </>
  );
};

const QuestionsComponent = ({
  title,
  subTitle,
  name: optionLabel,
  condition,
  options,
  type,
  slider,
  setStep,
  step,
  stepLength,
  setAnswers,
  answers,
  track,
}) => {
  const handleClick = (text, param, label) => {
    const updatedAnswers = [...answers, { text, param, label }];
    setAnswers(updatedAnswers);
    if (condition && condition !== param) {
      // condition is not used
      setStep(prev => prev + 2);
    } else {
      setStep(prev => prev + 1);
    }
    // Energiesparcheck STEP useTracking - last step tracked separate
    if (step < stepLength) {
      track({
        event: 'WarmwasserCheck',
        answers: updatedAnswers?.map(answer => answer?.text),
        steps: step,
      });
    }
  };
  const handleProgressBarClick = id => {
    setStep(id);
    const adjustetAnswers = answers;
    adjustetAnswers.length = id - 1;
    setAnswers(adjustetAnswers);
  };

  return (
    <div aria-label="Energiesparcheck" className={styles.container}>
      <Container className={styles.questions} size="lg">
        <ProgressBar
          currentStep={step}
          noLabel
          onClick={handleProgressBarClick}
          steps={questions}
        />
        <Heading
          as="h3"
          autofocus
          className={styles.title}
          size="h3"
          tabIndex={0}
        >
          {title}
        </Heading>
        {subTitle && <Content noSpacing>{subTitle}</Content>}
        <div className={classNames(styles.optionsWrapper, styles[type])}>
          <OptionDecider
            name={optionLabel}
            onClick={handleClick}
            options={options}
            slider={slider}
            type={type}
          />
        </div>
      </Container>
    </div>
  );
};

const WarmWasserCheck = ({ mainPosts, blogLink }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const track = useTracking();

  const [storage, setStorage] = useSessionStorage('show-conditional', 'hide');

  const questionsToRender = questions?.map((item, index) => ({
    ...item,
    order: index + 1,
  }));

  const setStorageInitial = () => {
    setStorage('hide');
  };

  useEffect(() => {
    window.addEventListener('beforeunload', setStorageInitial);

    return () => {
      window.removeEventListener('beforeunload', setStorageInitial);
    };
  }, []);

  useEffect(() => {
    if (step === questionsToRender.length + 1) {
      const finalResults = getResultsWasser(answers);
      setResults(finalResults);
      setStorage('show');
      // WarmwasserCheck send
      track({
        event: 'WarmwasserCheck',
        answers: answers?.map(answer => answer.text),
        steps: 'send',
      });

      // WarmwasserCheck Ergebnis
      track({
        event: 'WarmwasserCheck',
        answers: `${finalResults?.verbrauch} 'ungefährer Wasser- bzw.Stromverbrauch'`,
        steps: 'result',
      });
    }
  }, [step]);

  const [CurrentStepComponent] = questionsToRender.filter(
    ({ order }) => order === step,
  );

  return (
    <>
      {CurrentStepComponent && (
        <QuestionsComponent
          answers={answers}
          setAnswers={setAnswers}
          setStep={setStep}
          step={step}
          stepLength={questionsToRender.length}
          track={track}
          {...questions[step - 1]}
        />
      )}
      {results && (
        <FinalStep mainPosts={mainPosts} {...results} blogLink={blogLink} />
      )}
    </>
  );
};

export default WarmWasserCheck;
